import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { Message } from "types/Message";
import { MessagesWriteError } from "types/MessagesWriteError";
import { putThreadMessages } from "../methods/putThreadMessages";
import usePutMessagesMutation from "./usePutMessagesMutation";

export interface UseCommitMessagesArgs {
  /** The write-error screen's heading, e.g. "Couldn't save your messages". */
  errorTitle: string;
  /** The write-error screen's explanation of the failure. */
  errorDetail: string;
}

/**
 * Returns `commitMessages(threadId, messages)`, which writes the messages cache,
 * then PUTs the list. It never rejects. A PUT that fails after its retries
 * throws a `MessagesWriteError` during render (ADR 065), so call this from a
 * component that stays mounted until the PUT settles.
 */
export default function useCommitMessages({
  errorTitle,
  errorDetail,
}: UseCommitMessagesArgs) {
  const queryClient = useQueryClient();
  const {
    mutateAsync: putMessages,
    isError: isPutMessagesError,
    variables: failedPutMessages,
  } = usePutMessagesMutation();
  // The default token scope, which `usePutMessagesMutation` also uses.
  const getAccessToken = useGetAccessToken();

  const commitMessages = React.useCallback(
    async (threadId: string, messages: Message[]) => {
      queryClient.setQueryData<{ messages: Message[] }>(
        ["cc-db-api", "threads", threadId, "messages"],
        { messages }
      );
      try {
        await putMessages({ threadId, messages });
      } catch (error) {
        // The throw below needs a mounted caller, so log here as well.
        console.error("Couldn't save messages", { threadId, error });
      }
    },
    [queryClient, putMessages]
  );

  if (isPutMessagesError) {
    throw new MessagesWriteError({
      title: errorTitle,
      detail: errorDetail,
      // The cache already holds the list, so Retry only re-sends the PUT.
      retry: () =>
        putThreadMessages(
          failedPutMessages.threadId,
          failedPutMessages.messages,
          getAccessToken
        ),
    });
  }

  return commitMessages;
}
